// src/components/study/StudyPlan.jsx - COMPLETE REVISED FILE
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import { Calendar, Clock, Target, CheckCircle, Lock, BarChart, BookOpen, Zap, ChevronLeft, ChevronRight, GraduationCap, Crown, ArrowRight, X, AlertCircle } from 'lucide-react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { useUserStats } from '../../hooks/useUserStats'; // Import the hook to get real user data
import adminService from '../../services/adminService';

const StudyPlan = ({ activePlan, userProgress, onStartActivity, onReset, onVariationChange, subscriptionStatus, isLoadingSubscription }) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const location = useLocation();
  const [showPremiumOverlay, setShowPremiumOverlay] = useState(false);
  const [dismissedAlert, setDismissedAlert] = useState(false);
  // Use the hook to get live user stats
  const { userStats, loading: statsLoading } = useUserStats();

  // Check if user has premium access (admins always have access)
  const isAdmin = adminService.isAdmin();
  const hasPremium = isAdmin || (!isLoadingSubscription && subscriptionStatus && !subscriptionStatus.isFree && subscriptionStatus.isActive);

  // Get timelineAlert from router state or activePlan
  const timelineAlert = location.state?.alert || activePlan?.timelineAlert;

  // Show premium overlay when component mounts if user doesn't have premium
  React.useEffect(() => {
    if (!isLoadingSubscription && (!hasPremium)) {
      setShowPremiumOverlay(true);
    }
  }, [isLoadingSubscription, hasPremium]);

  // Debug: Log location changes
  React.useEffect(() => {
    console.log('📍 StudyPlan location changed:', location.pathname);
  }, [location.pathname]);

  if (!activePlan || !activePlan.weeks) {
    console.log('StudyPlan: activePlan or weeks missing', { 
      activePlan, 
      hasWeeks: !!activePlan?.weeks,
      weeksLength: activePlan?.weeks?.length,
      planKeys: activePlan ? Object.keys(activePlan) : []
    });
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 safe-area-inset">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
          </div>
          <p className="text-lg sm:text-xl text-black/60">{t('studyPlan.loading')}</p>
          <p className="text-sm text-black/40 mt-2">{t('studyPlan.waitingForPlan')}</p>
          <button
            onClick={onReset}
            className="mt-4 px-4 py-2 text-sm text-black/60 hover:text-black underline"
          >
            {t('studyPlan.startOver')}
          </button>
        </div>
      </div>
    );
  }

  const { weeks, targetExam, targetLevel } = activePlan;
  
  // Check if weeks array is empty
  if (!weeks || weeks.length === 0) {
    console.log('StudyPlan: weeks array is empty', { weeks, weeksLength: weeks?.length });
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 safe-area-inset">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
          </div>
          <p className="text-lg sm:text-xl text-black/60">{t('studyPlan.loading')}</p>
          <p className="text-sm text-black/40 mt-2">{t('studyPlan.noWeeksData')}</p>
        </div>
      </div>
    );
  }

  const [currentWeek, setCurrentWeek] = useState(1);
  
  // Ensure currentWeek is within valid range
  const safeCurrentWeek = Math.max(1, Math.min(currentWeek, weeks.length));
  const weekData = weeks[safeCurrentWeek - 1];
  const [hoveredButtonId, setHoveredButtonId] = useState(null);

  // Guard clause: if weekData doesn't exist, show loading state
  // But allow empty tasks array (some weeks might not have tasks yet)
  if (!weekData) {
    console.warn('StudyPlan: Week data not found', { 
      safeCurrentWeek, 
      totalWeeks: weeks.length, 
      weeksData: weeks.map((w, i) => ({ week: i + 1, hasData: !!w, hasTasks: !!w?.tasks }))
    });
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4 safe-area-inset">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-black/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-black" />
          </div>
          <p className="text-lg sm:text-xl text-black/60">{t('studyPlan.loading')}</p>
          <p className="text-sm text-black/40 mt-2">{t('studyPlan.loadingWeek', { current: safeCurrentWeek, total: weeks.length })}</p>
        </div>
      </div>
    );
  }

  // Ensure tasks is an array (default to empty array if missing)
  // This allows weeks with no tasks to still render
  const weekTasks = Array.isArray(weekData.tasks) ? weekData.tasks : [];

  const [mainContentRef, mainContentIsVisible] = useScrollAnimation();
  const [sidebarRef, sidebarIsVisible] = useScrollAnimation();

  // --- BUG FIX: Calculate real stats from user data ---
  const stats = userStats?.profile?.stats || {};
  const completedWeeksCount = userProgress?.completedWeeks?.length || 0;
  const totalWeeksCount = weeks.length;
  const remainingWeeks = totalWeeksCount - completedWeeksCount;
  const overallProgress = totalWeeksCount > 0 ? Math.round((completedWeeksCount / totalWeeksCount) * 100) : 0;
  const overallAccuracy = stats.totalQuestionsAttempted > 0 ? Math.round((stats.totalCorrectAnswers / stats.totalQuestionsAttempted) * 100) : 0;
  const totalStudyHours = Math.round((stats.totalStudyTimeMinutes || 0) / 60);

  const taskIcons = {
    vocabulary: <Target className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    grammar: <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    grammar_lessons: <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    grammar_exercises: <Target className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    exam_lessons: <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    reading: <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    listening: <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    writing: <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    speaking: <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />,
    lessons: <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
  };

  const hasExamLessons = weekTasks.some((task) => task.type === 'exam_lessons');
  const enhancedTasks = hasExamLessons
    ? weekTasks
    : [
        ...weekTasks,
        {
          id: `${safeCurrentWeek}-lessons`,
          type: 'lessons',
          title: t('studyPlan.structuredLessons.title'),
          description: t('studyPlan.structuredLessons.description', { week: safeCurrentWeek })
        }
      ];

  return (
    <div className="relative min-h-screen bg-white safe-area-inset">
      {/* Premium Overlay - Shows for free users */}
      {showPremiumOverlay && !hasPremium && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl border-2 border-black max-w-md w-full p-8 relative">
            <button
              onClick={() => navigate('/dashboard')}
              className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-black/10 rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-10 h-10 text-black" />
              </div>
              <div className="space-y-2">
                <h2 className="text-2xl sm:text-3xl font-bold text-black">
                  {t('premium.required.title')}
                </h2>
                <p className="text-lg text-black/60">
                  {t('premium.required.description')}
                </p>
              </div>
              <div className="space-y-3 pt-4">
                <button
                  onClick={() => navigate('/pricing')}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-lg font-semibold hover:bg-[#3A86FF] transition-all duration-300 shadow-lg hover:shadow-xl"
                >
                  <Crown className="w-5 h-5" />
                  {t('premium.required.upgrade')}
                  <ArrowRight className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full px-6 py-3 text-black/60 hover:text-black transition-colors duration-300"
                >
                  {t('premium.required.back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Timeline Alert Banner - Non-blocking dismissible warning */}
      {timelineAlert && !dismissedAlert && (
        <div className="relative z-40 bg-yellow-50 border-b-2 border-yellow-400 px-4 sm:px-6 lg:px-8 py-4">
          <div className="max-w-7xl mx-auto flex items-start gap-4">
            <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm sm:text-base font-semibold text-yellow-900 mb-1">
                {t('studyPlan.timelineRecommendation')}
              </h3>
              <p className="text-sm sm:text-base text-yellow-800">
                {timelineAlert.message || t('studyPlan.timelineRecommendationDesc')}
              </p>
            </div>
            <button
              onClick={() => setDismissedAlert(true)}
              className="flex-shrink-0 p-1 rounded-lg hover:bg-yellow-100 transition-colors"
              aria-label="Dismiss alert"
            >
              <X className="w-5 h-5 text-yellow-600" />
            </button>
          </div>
        </div>
      )}
      
      <div className="relative z-10 px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end mb-6">
            <button
              onClick={onReset}
              className="relative overflow-hidden flex items-center justify-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 text-sm sm:text-base font-semibold text-black border-2 border-black rounded-lg touch-manipulation min-h-[44px] min-w-[44px] active:scale-[0.98] active:bg-gray-50 transition-all duration-200 group cursor-pointer"
            >
              <span className="absolute inset-0 origin-left scale-x-0 bg-black transition-transform duration-700 ease-out sm:group-hover:scale-x-100" />
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 transition-colors duration-300 sm:group-hover:text-white" />
              <span className="hidden sm:inline relative z-10 transition-colors duration-300 sm:group-hover:text-white">{t('studyPlan.resetPlanLong')}</span>
              <span className="sm:hidden relative z-10 transition-colors duration-300">{t('studyPlan.resetPlanShort')}</span>
            </button>
          </div>
          <div className="flex flex-col lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start space-y-6 lg:space-y-0">
            <div ref={mainContentRef} className={`lg:col-span-2 space-y-6 sm:space-y-8 transition-all duration-700 ease-in-out ${mainContentIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`}>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-black p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6"><h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-black">{t('studyPlan.weekOverview')}</h2>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setCurrentWeek(Math.max(1, safeCurrentWeek - 1))} disabled={safeCurrentWeek === 1} className="p-2 sm:p-2.5 rounded-lg border-2 border-black sm:hover:bg-black sm:hover:text-white disabled:opacity-40 disabled:sm:hover:bg-white touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-[0.98] active:bg-gray-50 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"><ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                    <span className="px-3 py-1.5 sm:px-4 sm:py-2 bg-black text-white rounded-lg text-sm sm:text-base font-semibold min-w-[80px] text-center">{t('studyPlan.week', { week: safeCurrentWeek })}</span>
                    <button onClick={() => setCurrentWeek(Math.min(weeks.length, safeCurrentWeek + 1))} disabled={safeCurrentWeek === weeks.length} className="p-2 sm:p-2.5 rounded-lg border-2 border-black sm:hover:bg-black sm:hover:text-white disabled:opacity-40 disabled:sm:hover:bg-white touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center active:scale-[0.98] active:bg-gray-50 transition-all duration-200 cursor-pointer disabled:cursor-not-allowed"><ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" /></button>
                  </div>
                </div>
                <div className="mb-4 sm:mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {weeks.slice(0, 4).map((week, index) => (<button key={index} onClick={() => setCurrentWeek(index + 1)} className={`p-3 sm:p-4 rounded-lg border-2 transition-all duration-200 touch-manipulation min-h-[60px] cursor-pointer active:scale-[0.98] active:bg-gray-50 ${safeCurrentWeek === index + 1 ? 'bg-black text-white border-black transform scale-105' : 'bg-white border-black/20 sm:hover:border-black sm:hover:bg-black/5'}`}><div className="text-xs sm:text-sm font-medium opacity-80 mb-1">{t('studyPlan.week', { week: index + 1 })}</div><div className="text-sm sm:text-base font-bold truncate">{week.title || t('studyPlan.loading')}</div></button>))}
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-black p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6"><h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-black">{t('studyPlan.week', { week: safeCurrentWeek })}: {weekData.title || t('studyPlan.loading')}</h2><span className="px-3 py-1.5 bg-black text-white rounded-full text-sm font-semibold self-start sm:self-center">{t('studyPlan.current')}</span></div>
                <div className="mb-6"><h3 className="font-semibold text-black mb-3 text-base sm:text-lg">{t('studyPlan.focusAreas')}</h3><div className="flex flex-wrap gap-2">{(Array.isArray(weekData.focus) ? weekData.focus : []).map((focus, index) => (<span key={index} className="px-3 py-1.5 bg-white border-2 border-black text-black rounded-full text-sm sm:text-base">{focus}</span>))}</div></div>
                <div className="space-y-4"><h3 className="font-semibold text-black text-base sm:text-lg">{t('studyPlan.yourTasks')}</h3>
                  {enhancedTasks.map((task) => (
                    <div key={task.id} className="border-2 border-black rounded-xl p-4 sm:p-5 sm:hover:shadow-md transition-all duration-200 active:scale-[0.98] active:bg-gray-50 group">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                        <div className="flex items-start sm:items-center gap-3 sm:gap-4 flex-1 min-w-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-black/5 border-2 border-black">{taskIcons[task.type] || <Target className="w-5 h-5 sm:w-6 sm:h-6 text-black" />}</div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-bold text-base sm:text-lg text-black">{task.title}</h4>
                              {task.type === 'lessons' && (<span className="text-xs sm:text-sm bg-black text-white px-2 py-1 rounded-full">{t('studyPlan.newBadge')}</span>)}
                              {task.lessonIds && task.lessonIds.length > 0 && (
                                <span className="text-xs sm:text-sm bg-blue-500 text-white px-2 py-1 rounded-full">
                                  {task.lessonIds.length} {t('studyPlan.lessons')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm sm:text-base leading-relaxed text-black/70">{task.description}</p>
                            {task.estimatedTime && (<p className="text-xs sm:text-sm text-black/60 mt-1">⏱️ {task.estimatedTime}</p>)}
                            {task.lessons && task.lessons.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {task.lessons.slice(0, 3).map((lesson, idx) => (
                                  <div key={idx} className="text-xs text-black/50 flex items-center gap-1">
                                    <span>•</span>
                                    <span className="truncate">{lesson.title}</span>
                                  </div>
                                ))}
                                {task.lessons.length > 3 && (
                                  <div className="text-xs text-black/50">+ {task.lessons.length - 3} {t('studyPlan.more')}</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={() => {
                            console.log('=== StudyPlan Start Button Clicked ===');
                            console.log('Task object:', task);
                            console.log('Task type:', task.type);
                            console.log('Task ID:', task.id);
                            console.log('Task title:', task.title);
                            console.log('Task lessonIds:', task.lessonIds);
                            console.log('Target Exam:', targetExam);
                            console.log('Target Level:', targetLevel);
                            console.log('onStartActivity function:', typeof onStartActivity);
                            
                            // Direct navigation based on task type - check type first
                            switch (task.type) {
                              case 'vocabulary':
                                console.log('✅ Navigating to /practice/vocabulary');
                                console.log('Current location before navigate:', location.pathname);
                                try {
                                  navigate('/practice/vocabulary', { replace: false });
                                  console.log('✅ navigate() called successfully for vocabulary');
                                } catch (error) {
                                  console.error('❌ Error calling navigate:', error);
                                }
                                return;
                              
                              case 'grammar_lessons':
                                console.log('✅ Navigating to /study/grammar');
                                console.log('Current location before navigate:', location.pathname);
                                try {
                                  navigate('/study/grammar', { replace: false });
                                  console.log('✅ navigate() called successfully for grammar_lessons');
                                } catch (error) {
                                  console.error('❌ Error calling navigate:', error);
                                }
                                return;
                              
                              case 'grammar_exercises':
                                console.log('✅ Navigating to /practice/grammar');
                                console.log('Current location before navigate:', location.pathname);
                                try {
                                  navigate('/practice/grammar', { replace: false });
                                  console.log('✅ navigate() called successfully for grammar_exercises');
                                } catch (error) {
                                  console.error('❌ Error calling navigate:', error);
                                }
                                return;
                              
                              case 'redemittel':
                                console.log('✅ Navigating to /practice/redemittel');
                                console.log('Current location before navigate:', location.pathname);
                                try {
                                  navigate('/practice/redemittel', { replace: false });
                                  console.log('✅ navigate() called successfully for redemittel');
                                } catch (error) {
                                  console.error('❌ Error calling navigate:', error);
                                }
                                return;
                              
                              case 'exam_lessons':
                                console.log('📚 Handling exam_lessons task');
                                // If task has lesson references, navigate to study plan page with those lessons
                                if (task.lessonIds && task.lessonIds.length > 0 && targetExam && targetLevel) {
                                  console.log('✅ Navigating to /lessons with lesson highlighting');
                                  // Save exam data so StudyPlanPage can load the correct plan
                                  const examData = {
                                    examProvider: targetExam,
                                    level: targetLevel
                                  };
                                  const userId = userStats?.profile?.userId;
                                  if (userId) {
                                    localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
                                  }
                                  sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
                                  
                                  // Navigate with lesson IDs in state for potential highlighting
                                  navigate('/lessons', {
                                    state: {
                                      lessonIds: task.lessonIds,
                                      examProvider: targetExam,
                                      examLevel: targetLevel,
                                      highlightLessons: true
                                    }
                                  });
                                } else {
                                  console.log('⚠️ Navigating to /lessons without highlighting (missing lessonIds or exam data)');
                                  // Fallback: navigate to lessons page without highlighting
                                  const examData = {
                                    examProvider: targetExam,
                                    level: targetLevel
                                  };
                                  const userId = userStats?.profile?.userId;
                                  if (userId) {
                                    localStorage.setItem(`selectedExamData_${userId}`, JSON.stringify(examData));
                                  }
                                  sessionStorage.setItem('selectedExamData', JSON.stringify(examData));
                                  navigate('/lessons');
                                }
                                return;
                              
                              default:
                                console.log('⚠️ Task type not matched, falling back to onStartActivity:', task.type);
                                console.log('Calling onStartActivity with type:', task.type);
                                // Fallback to original behavior for other types
                                if (onStartActivity) {
                                  onStartActivity(task.type);
                                } else {
                                  console.error('❌ onStartActivity is not defined!');
                                }
                                return;
                            }
                          }}
                          onMouseEnter={() => setHoveredButtonId(task.id)}
                          onMouseLeave={() => setHoveredButtonId(null)}
                          className="relative overflow-hidden w-full sm:w-auto px-5 py-2.5 sm:py-3 rounded-lg font-semibold touch-manipulation min-h-[44px] min-w-[44px] flex items-center justify-center text-sm sm:text-base border-2 border-black text-black cursor-pointer transition-all duration-300 active:scale-[0.98] active:bg-gray-50 sm:hover:shadow-lg"
                        >
                          {/* Wave fill effect (Left-to-Right) - Only on hover, not touch */}
                          <div
                            className="hidden sm:block"
                            style={{
                              position: 'absolute',
                              top: 0,
                              bottom: 0,
                              left: 0,
                              width: hoveredButtonId === task.id ? '100%' : '0%',
                              background: 'linear-gradient(to right, #9ca3af, #4b5563, #000000)',
                              transition: 'width 0.7s ease-out',
                              zIndex: 0
                            }}
                          />
                          <span
                            className="relative z-10 transition-colors duration-500 sm:transition-colors sm:duration-500"
                            style={{ color: hoveredButtonId === task.id ? '#ffffff' : '#000000' }}
                          >
                            {t('button.start')}
                          </span>
                        </button>
                      </div>
                    </div>))}
                </div>
              </div>
            </div>

            <div ref={sidebarRef} className={`space-y-6 mb-6 lg:mb-0 lg:space-y-8 transition-all duration-700 ease-in-out lg:sticky lg:top-24 ${sidebarIsVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`}>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-black p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4 sm:mb-6"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center"><BarChart className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div><h3 className="text-lg sm:text-xl font-bold text-black">{t('studyPlan.progress.title')}</h3></div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center"><span className="text-sm sm:text-base text-black/60">{t('studyPlan.progress.overall')}</span><span className="text-base sm:text-lg font-bold text-black">{overallProgress}%</span></div>
                  <div className="w-full bg-black/10 rounded-full h-2.5 sm:h-3"><div className="bg-black h-2.5 sm:h-3 rounded-full" style={{ width: `${overallProgress}%` }}></div></div>
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-2">
                    <div className="text-center p-3 bg-white border-2 border-black rounded-lg"><div className="text-lg sm:text-xl font-bold text-black">{completedWeeksCount}</div><div className="text-xs sm:text-sm text-black/60">{t('studyPlan.progress.weeksCompleted')}</div></div>
                    <div className="text-center p-3 bg-white border-2 border-black rounded-lg"><div className="text-lg sm:text-xl font-bold text-black">{remainingWeeks}</div><div className="text-xs sm:text-sm text-black/60">{t('studyPlan.progress.weeksRemaining')}</div></div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-black p-4 sm:p-6">
                <div className="flex items-center gap-3 mb-4"><div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center"><Target className="w-4 h-4 sm:w-5 sm:h-5 text-white" /></div><h3 className="text-lg sm:text-xl font-bold text-black">{t('studyPlan.tip.title')}</h3></div><p className="text-sm sm:text-base text-black/70 leading-relaxed">{t('studyPlan.tip.text')}</p></div>
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-black p-4 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold text-black mb-4 sm:mb-6">{t('studyPlan.stats.title')}</h3>
                {statsLoading ? <p>{t('studyPlan.stats.loading')}</p> : (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-2 border-b border-black/10 last:border-0"><span className="text-sm sm:text-base text-black/60">{t('studyPlan.stats.totalStudyTime')}</span><span className="text-base sm:text-lg font-bold text-black">{totalStudyHours}h</span></div>
                    <div className="flex justify-between items-center py-2 border-b border-black/10 last:border-0"><span className="text-sm sm:text-base text-black/60">{t('studyPlan.stats.avgAccuracy')}</span><span className="text-base sm:text-lg font-bold text-black">{overallAccuracy}%</span></div>
                    <div className="flex justify-between items-center py-2 border-b border-black/10 last:border-0"><span className="text-sm sm:text-base text-black/60">{t('studyPlan.stats.completedExercises')}</span><span className="text-base sm:text-lg font-bold text-black">{stats.practiceSessionsCompleted}</span></div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudyPlan;